import {
  Button,
  Divider,
  Input,
  makeStyles,
  Spinner,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  ArrowUpload24Regular,
  BotAddRegular,
  Settings20Regular,
} from '@fluentui/react-icons';
import React, { useCallback, useState } from 'react';
import { useRecordMeetingMutation } from '../../queries/recording';
import { useGetMetadataQuery } from '../../queries/recording';
import { StatsPopover } from '../stats-popover';
import { PreferencesPopover } from '../preferences-popover';
import { UploadAudioDialog } from '../upload-audio-dialog';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    padding: `0 ${tokens.spacingHorizontalXL}`,
    height: '68px',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `0 1px 4px ${tokens.colorNeutralShadowAmbient}`,
    position: 'relative',
    zIndex: 10,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flex: 1
  },
  logoText: {
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase500,
    background: `linear-gradient(135deg, ${tokens.colorBrandForeground1}, ${tokens.colorBrandForeground2})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  },
  inviteForm: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flex: 1,
    maxWidth: '640px',
  },
  inviteInput: {
    flex: 1,
    '& input': {
      borderRadius: tokens.borderRadiusLarge,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
  },
  runningIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorPaletteGreenForeground1,
    animation: 'pulse 2s ease-in-out infinite',
  },
});

export const AppHeader = () => {
  const styles = useStyles();
  const { data: metadata } = useGetMetadataQuery();
  const { mutate, isPending: isSubmitPending } = useRecordMeetingMutation();
  const [inviteLink, setInviteLink] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const isDisabled = metadata?.user.hasRunningRecording || false;

  const handleSubmit = useCallback(() => {
    if (inviteLink.trim()) {
      mutate({ meetingUrl: inviteLink });
      setInviteLink('');
    }
  }, [inviteLink, mutate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && inviteLink.trim() && !isDisabled) {
        handleSubmit();
      }
    },
    [handleSubmit, inviteLink, isDisabled]
  );

  return (
    <header className={styles.header}>
      <div className={styles.logo}>

      </div>



      <div className={styles.inviteForm}>
        <Input
          className={styles.inviteInput}
          placeholder="Coller le lien d'invitation Teams pour inviter le bot..."
          value={inviteLink}
          onChange={(_, data) => setInviteLink(data.value)}
          onKeyDown={handleKeyDown}
          disabled={isDisabled || isSubmitPending}
          contentAfter={
            isSubmitPending ? (
              <Spinner size="tiny" />
            ) : undefined
          }
        />
        <Tooltip
          content={
            isDisabled
              ? 'Un enregistrement est deja en cours'
              : 'Inviter le bot a rejoindre la réunion'
          }
          relationship="label"
        >
          <Button
            appearance="primary"
            icon={<BotAddRegular />}
            disabled={isDisabled || isSubmitPending || !inviteLink.trim()}
            onClick={handleSubmit}
          >
            Inviter
          </Button>
        </Tooltip>
        <Tooltip content="Importer un fichier audio" relationship="label">
          <Button
            appearance="secondary"
            icon={<ArrowUpload24Regular />}
            disabled={isDisabled || isSubmitPending}
            onClick={() => setUploadDialogOpen(true)}
          >
            Importer
          </Button>
        </Tooltip>
      </div>

      {metadata?.user.hasRunningRecording && (
        <div className={styles.runningIndicator}>
          <Spinner size="extra-tiny" />
          <span>Enregistrement en cours</span>
        </div>
      )}

      <div className={styles.actions}>
        <StatsPopover />
        <PreferencesPopover />
      </div>

      <UploadAudioDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />
    </header>
  );
};
